import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { availabilityAPI } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarDays, Check } from "lucide-react";
import { format, addMonths, startOfMonth, endOfMonth } from "date-fns";

const statusColors = {
  available: "bg-green-100 text-green-800 border-green-200",
  booked: "bg-red-100 text-red-800 border-red-200",
  blocked: "bg-gray-100 text-gray-800 border-gray-200",
};

const statusLabels = {
  available: "Available",
  booked: "Booked",
  blocked: "Blocked",
};

export const AvailabilityCalendar = ({ 
  homestayId, 
  homestayName, 
  token, 
  isOpen, 
  onClose,
  isAdmin = false 
}) => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("booked");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (isOpen && homestayId) {
      fetchAvailability();
    }
  }, [isOpen, homestayId, currentMonth]);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), "yyyy-MM-dd");
      const end = format(endOfMonth(addMonths(currentMonth, 2)), "yyyy-MM-dd");
      
      const response = await availabilityAPI.get(homestayId, { 
        start_date: start, 
        end_date: end 
      });
      
      // Convert array to object keyed by date
      const availMap = {};
      response.data.forEach(item => {
        availMap[item.date] = item.status;
      });
      setAvailability(availMap);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return availability[dateStr] || "available";
  };

  const handleDateClick = (date) => {
    if (!isAdmin) return;
    
    const dateStr = format(date, "yyyy-MM-dd");
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      return [...prev, dateStr];
    });
  };

  const handleSave = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    setSaving(true);
    try {
      const dates = selectedDates.map(date => ({
        homestay_id: homestayId,
        date: date,
        status: selectedStatus
      }));
      
      await availabilityAPI.set(homestayId, dates, token);
      toast.success(`${selectedDates.length} date(s) marked as ${statusLabels[selectedStatus]}`);
      setSelectedDates([]);
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to update availability");
    } finally {
      setSaving(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
  };

  // Custom day renderer for the calendar
  const DayContent = ({ date }) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const status = getDateStatus(date);
    const isSelected = selectedDates.includes(dateStr);
    const isPast = date < new Date(new Date().setHours(0,0,0,0));

    return (
      <div 
        className={`
          w-full h-full flex items-center justify-center rounded-md text-sm relative
          ${isPast ? 'text-muted-foreground opacity-50' : ''}
          ${status === 'booked' ? 'bg-red-100 text-red-700' : ''}
          ${status === 'blocked' ? 'bg-gray-200 text-gray-600' : ''}
          ${isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}
          ${isAdmin && !isPast ? 'cursor-pointer hover:bg-primary/10' : ''}
        `}
        onClick={() => !isPast && handleDateClick(date)}
      >
        {date.getDate()}
        {isSelected && (
          <Check className="w-3 h-3 absolute -top-1 -right-1 text-primary" />
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Availability - {homestayName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white border"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
                <span>Booked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                <span>Blocked</span>
              </div>
            </div>

            {/* Calendar */}
            <div className="flex justify-center">
              <Calendar
                mode="multiple"
                selected={selectedDates.map(d => new Date(d))}
                onSelect={() => {}}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                numberOfMonths={2}
                className="rounded-md border"
                components={{
                  Day: ({ date }) => <DayContent date={date} />
                }}
              />
            </div>

            {/* Admin Controls */}
            {isAdmin && (
              <div className="border-t pt-4 space-y-4">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-medium">
                    {selectedDates.length} date(s) selected
                  </span>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={selectedStatus === "booked" ? "default" : "outline"}
                      onClick={() => setSelectedStatus("booked")}
                    >
                      Booked
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedStatus === "blocked" ? "default" : "outline"}
                      onClick={() => setSelectedStatus("blocked")}
                    >
                      Blocked
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedStatus === "available" ? "default" : "outline"}
                      onClick={() => setSelectedStatus("available")}
                    >
                      Available
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleClearSelection}
                    variant="outline"
                    disabled={selectedDates.length === 0}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={selectedDates.length === 0 || saving}
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilityCalendar;
